import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { dataService } from '@/lib/dataService'
import { supabase } from '@/lib/supabaseClient'
import { checkIsMockMode } from '@/lib/dataService'
import { getMockData, saveMockData } from '@/lib/mockHelper'

// ─── Type definitions ────────────────────────────────────────────────────────
interface ExamFile {
  id: string
  name: string
  description: string
  subject_id: number | null
  subject_name?: string
  file_name: string
  file_size: number
  file_type: string
  file_url: string        // blob URL (local) or Google Drive Link or Supabase URL
  storage_path?: string   // Supabase storage path if available
  drive_file_id?: string  // Google Drive File ID
  storage_type: 'local' | 'google_drive' // Storage type indicator
  uploaded_by: string
  uploader_name?: string
  created_at: string
  updated_at: string
  tags: string[]
}

// ─── IndexedDB helpers (stores actual file blobs locally) ─────────────────────
const DB_NAME = 'TesScoreExamDB'
const DB_VERSION = 2
const FILE_STORE = 'exam_blobs'
const META_STORE = 'exam_meta'

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(FILE_STORE)) db.createObjectStore(FILE_STORE, { keyPath: 'id' })
      if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE, { keyPath: 'id' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

const saveFileBlob = async (id: string, blob: Blob) => {
  const db = await openDB()
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(FILE_STORE, 'readwrite')
    tx.objectStore(FILE_STORE).put({ id, blob })
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

const getFileBlob = async (id: string): Promise<Blob | null> => {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FILE_STORE, 'readonly')
    const req = tx.objectStore(FILE_STORE).get(id)
    req.onsuccess = () => resolve(req.result?.blob || null)
    req.onerror = () => reject(req.error)
  })
}

const deleteFileBlob = async (id: string) => {
  const db = await openDB()
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(FILE_STORE, 'readwrite')
    tx.objectStore(FILE_STORE).delete(id)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

const saveAllMeta = async (files: ExamFile[]) => {
  const db = await openDB()
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readwrite')
    const store = tx.objectStore(META_STORE)
    store.clear()
    files.forEach(f => store.put({ ...f, file_url: f.storage_type === 'google_drive' ? f.file_url : '' })) 
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

const loadAllMeta = async (): Promise<ExamFile[]> => {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readonly')
    const req = tx.objectStore(META_STORE).getAll()
    req.onsuccess = () => resolve(req.result || [])
    req.onerror = () => reject(req.error)
  })
}

const deleteMeta = async (id: string) => {
  const db = await openDB()
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readwrite')
    tx.objectStore(META_STORE).delete(id)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

// ─── Format helpers ───────────────────────────────────────────────────────────
const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatDate = (iso: string) => {
  return new Date(iso).toLocaleDateString('th-TH', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

const getFileIcon = (type: string) => {
  if (type.includes('pdf')) return '📄'
  if (type.includes('word') || type.includes('docx') || type.includes('doc')) return '📝'
  return '📎'
}

const getFileBadgeStyle = (type: string) => {
  if (type.includes('pdf')) return { bg: 'rgba(239,68,68,0.12)', color: '#dc2626', label: 'PDF' }
  if (type.includes('word') || type.includes('docx') || type.includes('doc'))
    return { bg: 'rgba(59,130,246,0.12)', color: '#2563eb', label: 'Word' }
  return { bg: 'rgba(107,114,128,0.12)', color: '#6b7280', label: 'File' }
}

const generateUUID = () => {
  if (typeof window !== 'undefined' && window.crypto && (window.crypto as any).randomUUID) {
    return (window.crypto as any).randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

const isUUID = (str: string) => {
  const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return regex.test(str)
}

export const ExamRepository: React.FC = () => {
  const { user } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [files, setFiles] = useState<ExamFile[]>([])
  const [subjects, setSubjects] = useState<any[]>([])
  const [teachingAssignments, setTeachingAssignments] = useState<any[]>([])
  const [profiles, setProfiles] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // Google Drive states
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null)
  const [googleExpiry, setGoogleExpiry] = useState<number | null>(null)
  const [isGoogleConnected, setIsGoogleConnected] = useState(false)
  const [isGisLoaded, setIsGisLoaded] = useState(false)
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [filterSubject, setFilterSubject] = useState('')
  const [filterType, setFilterType] = useState('')

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadName, setUploadName] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadSubjectId, setUploadSubjectId] = useState<string>('')
  const [uploadTags, setUploadTags] = useState('')

  // Edit modal
  const [editingFile, setEditingFile] = useState<ExamFile | null>(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editSubjectId, setEditSubjectId] = useState<string>('')
  const [editTags, setEditTags] = useState('')

  // Delete confirm
  const [deletingFile, setDeletingFile] = useState<ExamFile | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Drag & drop
  const [isDragging, setIsDragging] = useState(false)
  const [subjectPage, setSubjectPage] = useState(1)
  const [selectedSubjectForModal, setSelectedSubjectForModal] = useState<any | null>(null)

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 5000)
  }

  // Load Google Client library
  useEffect(() => {
    if (!document.getElementById('google-gis-script')) {
      const script = document.createElement('script')
      script.id = 'google-gis-script'
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = () => setIsGisLoaded(true)
      document.body.appendChild(script)
    } else {
      setIsGisLoaded(true)
    }

    // Restore Google token if not expired
    const savedToken = localStorage.getItem('google_drive_access_token')
    const savedExpiry = localStorage.getItem('google_drive_token_expiry')
    const wasConnected = localStorage.getItem('google_drive_was_connected')
    if (savedToken && savedExpiry) {
      const exp = Number(savedExpiry)
      if (exp > Date.now()) {
        setGoogleAccessToken(savedToken)
        setGoogleExpiry(exp)
        setIsGoogleConnected(true)
      } else {
        // Token expired — clear token but keep was_connected flag for silent re-auth
        localStorage.removeItem('google_drive_access_token')
        localStorage.removeItem('google_drive_token_expiry')
      }
    }

    // Check if Supabase session has provider_token (Google access token from login)
    const checkSupabaseToken = async () => {
      if (checkIsMockMode()) return
      const { data } = await supabase.auth.getSession()
      if (data?.session?.provider_token) {
        const token = data.session.provider_token
        const exp = Date.now() + 3600 * 1000 // Supabase provider token is valid for 1 hour
        localStorage.setItem('google_drive_access_token', token)
        localStorage.setItem('google_drive_token_expiry', String(exp))
        localStorage.setItem('google_drive_was_connected', '1')
        setGoogleAccessToken(token)
        setGoogleExpiry(exp)
        setIsGoogleConnected(true)
      }
    }
    checkSupabaseToken()

    // Store that this user has connected before (for silent re-auth on reload)
    if (wasConnected) {
      // Will trigger silent reconnect once GIS is loaded (handled in isGisLoaded effect)
    }
  }, [])

  // ─── Google OAuth Flow ───────────────────────────────────────────────────
  const handleConnectGoogleDrive = () => {
    if (!googleClientId) {
      showNotification('error', '⚠️ ไม่พบ Google Client ID ในระบบคอนฟิกกรุณาตรวจสอบไฟล์ .env.local')
      return
    }

    if (!(window as any).google?.accounts?.oauth2) {
      showNotification('error', '⌛ กำลังโหลดระบบยืนยันสิทธิ์ของ Google กรุณารอสักครู่แล้วลองอีกครั้ง')
      return
    }

    try {
      const client = (window as any).google.accounts.oauth2.initTokenClient({
        client_id: googleClientId,
        scope: 'https://www.googleapis.com/auth/drive.file',
        callback: (response: any) => {
          if (response.error) {
            showNotification('error', `เชื่อมต่อ Google Drive ล้มเหลว: ${response.error_description || response.error}`)
            return
          }
          if (response.access_token) {
            const exp = Date.now() + Number(response.expires_in) * 1000
            localStorage.setItem('google_drive_access_token', response.access_token)
            localStorage.setItem('google_drive_token_expiry', String(exp))
            setGoogleAccessToken(response.access_token)
            setGoogleExpiry(exp)
            setIsGoogleConnected(true)
            showNotification('success', '🔌 เชื่อมต่อ Google Drive สำเร็จแล้ว!')
          }
        }
      })
      // prompt: '' = reuse existing consent silently; only shows consent screen on first time
      client.requestAccessToken({ prompt: '' })
      // Mark that this user has connected before for future silent re-auth
      localStorage.setItem('google_drive_was_connected', '1')
    } catch (err: any) {
      showNotification('error', `เกิดข้อผิดพลาดในการเชื่อมต่อ: ${err.message}`)
    }
  }

  // ─── Silent token refresh when GIS is ready ───────────────────────────────
  // Called automatically when the page loads and user was previously connected
  const silentRefreshGoogleToken = () => {
    if (!googleClientId) return
    const wasConnected = localStorage.getItem('google_drive_was_connected')
    const savedToken = localStorage.getItem('google_drive_access_token')
    const savedExpiry = localStorage.getItem('google_drive_token_expiry')
    // Only do silent refresh if: user was connected before AND token is missing/expired
    if (!wasConnected) return
    if (savedToken && savedExpiry && Number(savedExpiry) > Date.now()) return
    try {
      const client = (window as any).google?.accounts?.oauth2?.initTokenClient({
        client_id: googleClientId,
        scope: 'https://www.googleapis.com/auth/drive.file',
        callback: (response: any) => {
          if (response?.access_token) {
            const exp = Date.now() + Number(response.expires_in) * 1000
            localStorage.setItem('google_drive_access_token', response.access_token)
            localStorage.setItem('google_drive_token_expiry', String(exp))
            setGoogleAccessToken(response.access_token)
            setGoogleExpiry(exp)
            setIsGoogleConnected(true)
          }
        }
      })
      // prompt: 'none' = fully silent, no UI shown
      client?.requestAccessToken({ prompt: 'none' })
    } catch {
      // Silent refresh failed — user must manually reconnect
    }
  }

  const handleDisconnectGoogleDrive = () => {
    localStorage.removeItem('google_drive_access_token')
    localStorage.removeItem('google_drive_token_expiry')
    localStorage.removeItem('google_drive_was_connected') // Clear so silent re-auth won't trigger
    setGoogleAccessToken(null)
    setGoogleExpiry(null)
    setIsGoogleConnected(false)
    showNotification('success', '🔌 ตัดการเชื่อมต่อ Google Drive เรียบร้อยแล้ว')
  }

  // Auto silent-refresh: once GIS library is loaded and user was previously connected
  useEffect(() => {
    if (isGisLoaded) {
      silentRefreshGoogleToken()
    }
  }, [isGisLoaded])

  // ─── Load data ────────────────────────────────────────────────────────────
  const loadData = async () => {
    setLoading(true)
    try {
      const allSubjects = await dataService.getSubjects()
      setSubjects(allSubjects)

      const allAssignments = await dataService.getTeachingAssignments()
      setTeachingAssignments(allAssignments)

      const allProfiles = await dataService.getProfiles()
      setProfiles(allProfiles)

      const isMock = checkIsMockMode()
      let loadedMeta: ExamFile[] = []

      if (!isMock) {
        try {
          // Read from Supabase if connected
          const { data, error } = await supabase
            .from('exam_files')
            .select('*, profiles(first_name, last_name)')
            .order('created_at', { ascending: false })
          
          if (error) throw error

          if (data && data.length > 0) {
            loadedMeta = data.map((f: any) => ({
              id: f.id,
              name: f.name,
              description: f.description || '',
              subject_id: f.subject_id,
              subject_name: allSubjects.find((s: any) => s.id === f.subject_id)?.name || 'ทั่วไป',
              file_name: f.file_name,
              file_size: f.file_size,
              file_type: f.file_type,
              file_url: f.file_url || '',
              storage_path: f.storage_path || '',
              drive_file_id: f.drive_file_id || '',
              storage_type: f.storage_type || 'local',
              uploaded_by: f.uploaded_by,
              uploader_name: f.profiles ? `${f.profiles.first_name} ${f.profiles.last_name}` : 'ไม่ระบุ',
              created_at: f.created_at,
              updated_at: f.updated_at,
              tags: f.tags || []
            }))
          }
        } catch (dbErr) {
          console.warn('Supabase fetch failed, falling back to local storage:', dbErr)
          loadedMeta = await loadAllMeta()
        }
      } else {
        const mockExams = getMockData().examFiles || []
        loadedMeta = mockExams.map((f: any) => ({
          ...f,
          subject_name: allSubjects.find((s: any) => s.id === f.subject_id)?.name || 'ทั่วไป',
          uploader_name: allProfiles.find((p: any) => p.id === f.uploaded_by) 
            ? `${allProfiles.find((p: any) => p.id === f.uploaded_by).first_name} ${allProfiles.find((p: any) => p.id === f.uploaded_by).last_name}`
            : 'ไม่ระบุ'
        }))
      }

      if (loadedMeta.length === 0) {
        // Fallback to IndexedDB metadata if nothing from database
        const localMeta = await loadAllMeta()
        loadedMeta = localMeta.map(f => ({
          ...f,
          subject_name: allSubjects.find((s: any) => s.id === f.subject_id)?.name || 'ทั่วไป'
        }))
      }

      // Sort newest first
      loadedMeta.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      setFiles(loadedMeta)
    } catch (err: any) {
      showNotification('error', `โหลดข้อมูลล้มเหลว: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadData() }, [])

  // ─── Drag & Drop ──────────────────────────────────────────────────────────
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFileSelection(file)
  }

  // ─── File selection ───────────────────────────────────────────────────────
  const processFileSelection = (file: File) => {
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    if (!allowed.includes(file.type)) {
      showNotification('error', 'รองรับเฉพาะไฟล์ PDF และ Word (.doc, .docx) เท่านั้น')
      return
    }
    if (file.size > 50 * 1024 * 1024) {
      showNotification('error', 'ขนาดไฟล์ต้องไม่เกิน 50 MB')
      return
    }
    setSelectedFile(file)
    setUploadName(prev => prev || file.name.replace(/\.[^.]+$/, ''))

    // Auto select subject based on registered subjects
    const myAssignments = teachingAssignments.filter((a: any) => a.teacher_id === user?.id)
    const mySubjectIds = Array.from(new Set(myAssignments.map((a: any) => a.subject_id))) as number[]
    if (mySubjectIds.length === 1) {
      setUploadSubjectId(String(mySubjectIds[0]))
    } else {
      setUploadSubjectId('')
    }

    setShowUploadModal(true)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFileSelection(file)
    e.target.value = ''
  }

  // ─── Upload flow ─────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!selectedFile || !uploadName.trim() || !user) return
    setUploading(true)
    setUploadProgress(10)

    try {
      const now = new Date().toISOString()
      const newId = generateUUID()
      const isMock = checkIsMockMode()
      
      let storageType: 'local' | 'google_drive' = 'local'
      let driveFileId = ''
      let fileUrl = ''
      let storagePath: string | undefined = undefined

      // Check if Google Drive token is valid
      const isTokenValid = googleExpiry && googleExpiry > Date.now()

      if (isGoogleConnected && googleAccessToken && isTokenValid) {
        // ─── Google Drive Upload ───
        setUploadProgress(20)
        
        // ─── Check / Create TesScore Dedicated Folder ───
        let folderId = localStorage.getItem('google_drive_folder_id')
        
        const checkFolderExists = async (id: string) => {
          try {
            const res = await fetch(`https://www.googleapis.com/drive/v3/files/${id}?fields=id,trashed`, {
              headers: { Authorization: `Bearer ${googleAccessToken}` }
            })
            if (!res.ok) return false
            const data = await res.json()
            return !data.trashed
          } catch {
            return false
          }
        }

        let folderExists = false
        if (folderId) {
          folderExists = await checkFolderExists(folderId)
        }

        if (!folderExists) {
          // Search for existing folder with name "คลังข้อสอบ TesScore"
          try {
            const searchRes = await fetch(
              `https://www.googleapis.com/drive/v3/files?q=name='คลังข้อสอบ TesScore' and mimeType='application/vnd.google-apps.folder' and trashed=false&fields=files(id)`,
              { headers: { Authorization: `Bearer ${googleAccessToken}` } }
            )
            if (searchRes.ok) {
              const searchData = await searchRes.json()
              if (searchData.files && searchData.files.length > 0) {
                folderId = searchData.files[0].id
                localStorage.setItem('google_drive_folder_id', folderId!)
                folderExists = true
              }
            }
          } catch (err) {
            console.warn('Failed to search for folder:', err)
          }
        }

        if (!folderExists) {
          // Create the folder
          try {
            const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${googleAccessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                name: 'คลังข้อสอบ TesScore',
                mimeType: 'application/vnd.google-apps.folder'
              })
            })
            if (createRes.ok) {
              const createData = await createRes.json()
              folderId = createData.id
              localStorage.setItem('google_drive_folder_id', folderId!)
            }
          } catch (err) {
            console.warn('Failed to create folder:', err)
          }
        }

        // Metadata for Google Drive multipart upload
        const metadata = {
          name: `${uploadName.trim()} (${selectedFile.name})`,
          mimeType: selectedFile.type,
          description: uploadDescription.trim(),
          ...(folderId ? { parents: [folderId] } : {})
        }

        const boundary = 'foo_bar_baz'
        const delimiter = `\r\n--${boundary}\r\n`
        const closeDelimiter = `\r\n--${boundary}--`

        // Convert file to base64
        const reader = new FileReader()
        const fileBase64Promise = new Promise<string>((resolve) => {
          reader.onload = () => {
            const raw = reader.result as string
            const base64 = raw.split(',')[1]
            resolve(base64)
          }
          reader.readAsDataURL(selectedFile)
        })

        const base64Data = await fileBase64Promise
        setUploadProgress(40)

        const multipartBody = 
          delimiter +
          'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
          JSON.stringify(metadata) +
          delimiter +
          'Content-Type: ' + selectedFile.type + '\r\n' +
          'Content-Transfer-Encoding: base64\r\n\r\n' +
          base64Data +
          closeDelimiter

        const driveResponse = await fetch(
          'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink',
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${googleAccessToken}`,
              'Content-Type': `multipart/related; boundary=${boundary}`
            },
            body: multipartBody
          }
        )

        if (!driveResponse.ok) {
          const errMsg = await driveResponse.text()
          throw new Error(`Google Drive Upload failed: ${driveResponse.statusText} (${errMsg})`)
        }

        const driveResult = await driveResponse.json()
        driveFileId = driveResult.id
        fileUrl = driveResult.webViewLink || ''
        storageType = 'google_drive'
        setUploadProgress(80)
      } else {
        // ─── Local Fallback (IndexedDB) ───
        await saveFileBlob(newId, selectedFile)
        storageType = 'local'
        setUploadProgress(40)

        // Upload to Supabase Storage if not mock
        if (!isMock) {
          try {
            const fileExt = selectedFile.name.split('.').pop()
            const path = `${user.id}/${newId}.${fileExt}`
            storagePath = path

            const { error: uploadErr } = await supabase.storage
              .from('exam-repository')
              .upload(path, selectedFile, { cacheControl: '3600', upsert: true })

            if (uploadErr) throw uploadErr

            fileUrl = path
            setUploadProgress(75)
          } catch (storageErr: any) {
            console.error('Supabase Storage upload failed:', storageErr)
            throw new Error(`อัปโหลดไฟล์เข้าระบบคลาวด์ล้มเหลว: ${storageErr.message}`)
          }
        }
      }

      const subjectName = subjects.find(s => s.id === Number(uploadSubjectId))?.name || 'ทั่วไป'
      const newFile: ExamFile = {
        id: newId,
        name: uploadName.trim(),
        description: uploadDescription.trim(),
        subject_id: uploadSubjectId ? Number(uploadSubjectId) : null,
        subject_name: subjectName,
        file_name: selectedFile.name,
        file_size: selectedFile.size,
        file_type: selectedFile.type,
        file_url: fileUrl,
        storage_path: storagePath,
        drive_file_id: driveFileId,
        storage_type: storageType,
        uploaded_by: user.id,
        uploader_name: `${user.first_name} ${user.last_name}`,
        created_at: now,
        updated_at: now,
        tags: uploadTags.split(',').map(t => t.trim()).filter(Boolean)
      }

      // Save Metadata locally (IndexedDB)
      const existingLocal = await loadAllMeta()
      await saveAllMeta([newFile, ...existingLocal])

      // Save Metadata to Supabase or Mock localStore
      if (!isMock) {
        try {
          const { error } = await supabase.from('exam_files').insert({
            id: newId,
            name: newFile.name,
            description: newFile.description,
            subject_id: newFile.subject_id,
            file_name: newFile.file_name,
            file_size: newFile.file_size,
            file_type: newFile.file_type,
            file_url: newFile.file_url,
            storage_path: newFile.storage_path,
            drive_file_id: newFile.drive_file_id,
            storage_type: newFile.storage_type,
            uploaded_by: newFile.uploaded_by,
            tags: newFile.tags
          })
          if (error) throw error
        } catch (dbErr: any) {
          console.error('Failed to sync metadata to Supabase:', dbErr)
          showNotification('error', `⚠️ บันทึกข้อมูลคลาวด์ไม่สำเร็จ แต่จัดเก็บในเบราว์เซอร์แล้ว: ${dbErr.message}`)
        }
      } else {
        const mockData = getMockData()
        if (!mockData.examFiles) mockData.examFiles = []
        mockData.examFiles.unshift({
          id: newId,
          name: newFile.name,
          description: newFile.description,
          subject_id: newFile.subject_id,
          file_name: newFile.file_name,
          file_size: newFile.file_size,
          file_type: newFile.file_type,
          file_url: newFile.file_url,
          storage_path: newFile.storage_path,
          drive_file_id: newFile.drive_file_id,
          storage_type: newFile.storage_type,
          uploaded_by: newFile.uploaded_by,
          tags: newFile.tags,
          created_at: now,
          updated_at: now
        })
        saveMockData(mockData)
      }

      setUploadProgress(100)
      showNotification('success', `✅ อัปโหลดไฟล์ "${uploadName}" (${storageType === 'google_drive' ? 'Google Drive' : 'จัดเก็บในเครื่อง'}) สำเร็จแล้ว`)
      closeUploadModal()
      await loadData()
    } catch (err: any) {
      showNotification('error', `อัปโหลดล้มเหลว: ${err.message}`)
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setSelectedFile(null)
    setUploadName('')
    setUploadDescription('')
    setUploadSubjectId('')
    setUploadTags('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // ─── Edit ─────────────────────────────────────────────────────────────────
  const openEdit = (file: ExamFile) => {
    setEditingFile(file)
    setEditName(file.name)
    setEditDescription(file.description)
    setEditSubjectId(file.subject_id ? String(file.subject_id) : '')
    setEditTags(file.tags?.join(', ') || '')
  }

  const handleEdit = async () => {
    if (!editingFile || !editName.trim()) return
    try {
      const updatedData = {
        name: editName.trim(),
        description: editDescription.trim(),
        subject_id: editSubjectId ? Number(editSubjectId) : null,
        tags: editTags.split(',').map(t => t.trim()).filter(Boolean),
        updated_at: new Date().toISOString()
      }

      // Update IndexedDB
      const existing = await loadAllMeta()
      const idx = existing.findIndex(f => f.id === editingFile.id)
      if (idx !== -1) {
        existing[idx] = { ...existing[idx], ...updatedData }
        await saveAllMeta(existing)
      }

      // Update Supabase or Mock localStore
      const isMock = checkIsMockMode()
      if (!isMock) {
        const { error } = await supabase
          .from('exam_files')
          .update(updatedData)
          .eq('id', editingFile.id)
        if (error) throw error
      } else {
        const mockData = getMockData()
        if (mockData.examFiles) {
          const mIdx = mockData.examFiles.findIndex((f: any) => f.id === editingFile.id)
          if (mIdx !== -1) {
            mockData.examFiles[mIdx] = { ...mockData.examFiles[mIdx], ...updatedData }
            saveMockData(mockData)
          }
        }
      }

      showNotification('success', 'แก้ไขข้อมูลสำเร็จแล้ว')
      setEditingFile(null)
      await loadData()
    } catch (err: any) {
      showNotification('error', `แก้ไขล้มเหลว: ${err.message}`)
    }
  }

  // ─── Download flow ───────────────────────────────────────────────────────
  const handleDownload = async (file: ExamFile) => {
    try {
      if (file.storage_type === 'google_drive' && file.drive_file_id) {
        // Open webViewLink in a new tab (this is highly reliable and prompts Google Drive interface)
        if (file.file_url) {
          window.open(file.file_url, '_blank')
          return
        }
        
        // Fallback: If no direct URL, request via API if token available
        const isTokenValid = googleExpiry && googleExpiry > Date.now()
        if (googleAccessToken && isTokenValid) {
          const response = await fetch(`https://www.googleapis.com/drive/v3/files/${file.drive_file_id}?alt=media`, {
            headers: { Authorization: `Bearer ${googleAccessToken}` }
          })
          if (!response.ok) throw new Error('ไม่สามารถดาวน์โหลดไฟล์จาก Google Drive ได้')
          const blob = await response.blob()
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = file.file_name
          a.click()
          setTimeout(() => URL.revokeObjectURL(url), 3000)
          return
        }

        // If not connected, redirect to auth
        showNotification('error', '⚠️ กรุณาเชื่อมต่อ Google Drive เพื่อทำการดาวน์โหลดไฟล์นี้')
        return
      }

      // Local files
      let blob = await getFileBlob(file.id)
      const isMock = checkIsMockMode()
      if (!blob) {
        if (!isMock && file.storage_path) {
          showNotification('success', '⏳ กำลังดาวน์โหลดไฟล์จากคลาวด์...')
          try {
            const { data, error } = await supabase.storage
              .from('exam-repository')
              .download(file.storage_path)
            
            if (error) throw error
            if (data) {
              await saveFileBlob(file.id, data)
              blob = data
            }
          } catch (storageDlErr: any) {
            console.error('Failed to download from Supabase Storage:', storageDlErr)
            showNotification('error', `❌ ไม่สามารถดาวน์โหลดไฟล์จากเซิร์ฟเวอร์คลาวด์ได้: ${storageDlErr.message}`)
            return
          }
        }
      }

      if (!blob) {
        showNotification('error', '❌ ไม่พบไฟล์นี้บนเครื่องปัจจุบัน (ไฟล์อาจจัดเก็บอยู่ใน IndexedDB ของคุณครูท่านอื่นที่อัปโหลด)')
        return
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.file_name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 3000)
    } catch (err: any) {
      showNotification('error', `ดาวน์โหลดล้มเหลว: ${err.message}`)
    }
  }

  // ─── Preview flow ────────────────────────────────────────────────────────
  const handlePreview = async (file: ExamFile) => {
    try {
      if (file.storage_type === 'google_drive') {
        if (file.file_url) {
          window.open(file.file_url, '_blank')
        } else {
          showNotification('error', '⚠️ ไม่พบลิงก์ดูเอกสารของ Google Drive')
        }
        return
      }

      let blob = await getFileBlob(file.id)
      const isMock = checkIsMockMode()
      if (!blob) {
        if (!isMock && file.storage_path) {
          showNotification('success', '⏳ กำลังดาวน์โหลดไฟล์จากคลาวด์เพื่อเปิดดู...')
          try {
            const { data, error } = await supabase.storage
              .from('exam-repository')
              .download(file.storage_path)
            
            if (error) throw error
            if (data) {
              await saveFileBlob(file.id, data)
              blob = data
            }
          } catch (storageDlErr: any) {
            console.error('Failed to download from Supabase Storage for preview:', storageDlErr)
            showNotification('error', `❌ ไม่สามารถดาวน์โหลดไฟล์จากเซิร์ฟเวอร์คลาวด์ได้: ${storageDlErr.message}`)
            return
          }
        }
      }

      if (!blob) {
        showNotification('error', '❌ ไม่พบไฟล์บนเครื่องปัจจุบัน (ไฟล์ถูกจัดเก็บในเครื่องของคุณครูท่านอื่น)')
        return
      }
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (err: any) {
      showNotification('error', `เปิดไฟล์ล้มเหลว: ${err.message}`)
    }
  }

  // ─── Delete flow ─────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deletingFile) return
    setDeleting(true)
    try {
      // 1. Delete from Google Drive if needed
      if (deletingFile.storage_type === 'google_drive' && deletingFile.drive_file_id) {
        const isTokenValid = googleExpiry && googleExpiry > Date.now()
        if (googleAccessToken && isTokenValid) {
          try {
            await fetch(`https://www.googleapis.com/drive/v3/files/${deletingFile.drive_file_id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${googleAccessToken}` }
            })
          } catch (driveDelErr) {
            console.warn('Failed to delete file from Google Drive storage:', driveDelErr)
          }
        }
      }

      // 2. Delete local blob in IndexedDB
      await deleteFileBlob(deletingFile.id)

      // 3. Delete metadata in IndexedDB
      await deleteMeta(deletingFile.id)

      // Delete from Supabase Storage or Mock localStore
      const isMock = checkIsMockMode()
      if (!isMock) {
        if (deletingFile.storage_type === 'local' && deletingFile.storage_path) {
          try {
            await supabase.storage
              .from('exam-repository')
              .remove([deletingFile.storage_path])
          } catch (storageDelErr) {
            console.warn('Failed to delete file from Supabase storage:', storageDelErr)
          }
        }

        // 4. Delete metadata from Supabase
        if (isUUID(deletingFile.id)) {
          const { error } = await supabase
            .from('exam_files')
            .delete()
            .eq('id', deletingFile.id)
          if (error) throw error
        }
      } else {
        const mockData = getMockData()
        if (mockData.examFiles) {
          mockData.examFiles = mockData.examFiles.filter((f: any) => f.id !== deletingFile.id)
          saveMockData(mockData)
        }
      }

      showNotification('success', `ลบไฟล์ "${deletingFile.name}" สำเร็จแล้ว`)
      setDeletingFile(null)
      await loadData()
    } catch (err: any) {
      showNotification('error', `ลบล้มเหลว: ${err.message}`)
    } finally {
      setDeleting(false)
    }
  }

  // ─── Filter ───────────────────────────────────────────────────────────────
  const filteredFiles = files.filter(f => {
    // 1. Role-based visibility check
    const fileSubject = subjects.find(s => s.id === f.subject_id)
    const uploaderProfile = profiles.find(p => p.id === f.uploaded_by)
    const fileDeptId = fileSubject?.department_id || uploaderProfile?.department_id || null

    let isVisible = false
    if (user?.role === 'admin') {
      isVisible = true
    } else if (user?.role === 'head_of_department') {
      // HOD can see files in their department, or their own files
      isVisible = (fileDeptId !== null && fileDeptId === user?.department_id) || f.uploaded_by === user?.id
    } else {
      // Teachers can see all files school-wide (Read Only for others)
      isVisible = true
    }

    if (!isVisible) return false

    // 2. Query filter
    const q = searchQuery.toLowerCase()
    const matchSearch = !q || f.name.toLowerCase().includes(q) ||
      f.description.toLowerCase().includes(q) ||
      f.file_name.toLowerCase().includes(q) ||
      f.tags?.some(t => t.toLowerCase().includes(q))
    const matchSubject = !filterSubject || String(f.subject_id) === filterSubject
    const matchType = !filterType ||
      (filterType === 'pdf' && f.file_type.includes('pdf')) ||
      (filterType === 'word' && (f.file_type.includes('word') || f.file_type.includes('doc')))
    return matchSearch && matchSubject && matchType
  })

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px 16px', paddingBottom: '80px' }}>

      {/* Notification Toast */}
      {notification && (
        <div style={{
          position: 'fixed', top: '80px', right: '24px', zIndex: 1000,
          background: notification.type === 'success' ? 'rgba(16,185,129,0.97)' : 'rgba(239,68,68,0.97)',
          color: '#fff', padding: '14px 20px', borderRadius: '14px',
          fontSize: '14px', fontWeight: '600', maxWidth: '380px',
          backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: '10px',
          animation: 'slideInRight 0.3s ease'
        }}>
          <span style={{ fontSize: '20px' }}>{notification.type === 'success' ? '✅' : '❌'}</span>
          <span>{notification.message}</span>
        </div>
      )}

      {/* Page Header + Google Drive Connection Panel */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '28px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '14px',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '24px', flexShrink: 0, boxShadow: '0 4px 14px rgba(99,102,241,0.3)'
          }}>📂</div>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '800', margin: 0 }}>คลังข้อสอบ</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>
              อัปโหลด จัดการ และแชร์ไฟล์ข้อสอบขึ้น Google Drive หรือจัดเก็บในเครื่องอย่างปลอดภัย
            </p>
          </div>
        </div>

        {/* Google Drive Status Button (Completely automated for teachers) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isGoogleConnected ? (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              background: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.3)',
              borderRadius: '12px',
              padding: '6px 14px'
            }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }} />
                🌐 เชื่อมต่อ Google Drive สำเร็จ
              </span>
              <button 
                type="button" 
                onClick={handleDisconnectGoogleDrive}
                className="btn" 
                style={{ 
                  background: 'rgba(239,68,68,0.1)', 
                  color: 'var(--danger)', 
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                ตัดการเชื่อมต่อ
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleConnectGoogleDrive}
              className="btn"
              disabled={!isGisLoaded}
              style={{
                background: 'linear-gradient(135deg, #4285F4, #34A853)',
                color: '#fff',
                border: 'none',
                padding: '10px 18px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: isGisLoaded ? 'pointer' : 'not-allowed',
                boxShadow: '0 4px 14px rgba(66,133,244,0.25)',
                opacity: isGisLoaded ? 1 : 0.6
              }}
            >
              <span>🔑</span> เชื่อมต่อ Google Drive
            </button>
          )}
        </div>
      </div>

      {/* Top Row: Stats + Upload Button */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'ทั้งหมด', value: files.length, icon: '📂', color: '#6366f1', bg: 'rgba(99,102,241,0.08)' },
          { label: 'PDF', value: files.filter(f => f.file_type.includes('pdf')).length, icon: '📄', color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
          { label: 'Word', value: files.filter(f => f.file_type.includes('word') || f.file_type.includes('doc')).length, icon: '📝', color: '#2563eb', bg: 'rgba(37,99,235,0.08)' },
          { label: 'บน Google Drive', value: files.filter(f => f.storage_type === 'google_drive').length, icon: '🌐', color: '#34a853', bg: 'rgba(52,168,83,0.08)' },
        ].map((stat, i) => (
          <div key={i} style={{
            background: stat.bg, border: `1px solid ${stat.color}30`,
            borderRadius: '12px', padding: '12px 18px',
            display: 'flex', gap: '12px', alignItems: 'center', minWidth: '110px'
          }}>
            <span style={{ fontSize: '22px' }}>{stat.icon}</span>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '800', color: stat.color, lineHeight: 1 }}>{stat.value}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{stat.label}</div>
            </div>
          </div>
        ))}

        <div style={{ flex: 1 }} />

        {/* Upload Button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="btn btn-primary"
          style={{
            padding: '12px 24px', fontSize: '14px', fontWeight: '700',
            display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            boxShadow: '0 4px 14px rgba(99,102,241,0.35)', border: 'none'
          }}
        >
          <span style={{ fontSize: '18px' }}>⬆️</span>
          อัปโหลดข้อสอบ
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </div>

      {/* Filter Bar */}
      <div className="glassmorphism" style={{
        padding: '14px 18px', borderRadius: '12px', marginBottom: '20px',
        display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center'
      }}>
        <div style={{ flex: '2 1 200px', position: 'relative' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            placeholder="ค้นหาชื่อไฟล์, รายละเอียด, แท็ก..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="input-field"
            style={{ paddingLeft: '36px', width: '100%' }}
          />
        </div>
        <select
          value={filterSubject}
          onChange={e => setFilterSubject(e.target.value)}
          className="input-field"
          style={{ flex: '1 1 160px' }}
        >
          <option value="">📚 ทุกวิชา</option>
          {subjects.map((s: any) => (
            <option key={s.id} value={String(s.id)}>[{s.code}] {s.name}</option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="input-field"
          style={{ flex: '1 1 120px' }}
        >
          <option value="">📎 ทุกประเภท</option>
          <option value="pdf">📄 PDF</option>
          <option value="word">📝 Word</option>
        </select>
        {(searchQuery || filterSubject || filterType) && (
          <button type="button" className="btn btn-outline"
            onClick={() => { setSearchQuery(''); setFilterSubject(''); setFilterType('') }}
            style={{ fontSize: '12px', padding: '8px 12px' }}>
            ✕ ล้างตัวกรอง
          </button>
        )}
        {filteredFiles.length !== files.length && (
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            พบ {filteredFiles.length} จาก {files.length} ไฟล์
          </span>
        )}
      </div>

      {/* 📊 สรุปรายงานคลังข้อสอบแยกตามวิชาและครูผู้สอน */}
      <div className="glassmorphism" style={{ padding: '24px', marginBottom: '24px', background: 'var(--bg-card)' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '14px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📊 รายงานการอัปโหลดข้อสอบแยกตามรายวิชา
        </h2>
        <div className="custom-table-container">
          <table className="custom-table" style={{ fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ width: '130px' }}>รหัสวิชา</th>
                <th>ชื่อวิชา</th>
                <th style={{ width: '130px', textAlign: 'center' }}>จำนวนไฟล์</th>
                <th>ครูผู้ส่งข้อสอบ</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const subjectsPerPage = 10
                const paginatedSubjects = subjects.slice((subjectPage - 1) * subjectsPerPage, subjectPage * subjectsPerPage)
                
                if (paginatedSubjects.length === 0) {
                  return (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
                        ไม่มีวิชาสอนในระบบ
                      </td>
                    </tr>
                  )
                }

                return paginatedSubjects.map(sub => {
                  const subFiles = files.filter(f => f.subject_id === sub.id)
                  const uploaders = Array.from(new Set(subFiles.map(f => f.uploader_name || 'ไม่ระบุ')))
                  return (
                    <tr 
                      key={sub.id} 
                      onClick={() => setSelectedSubjectForModal(sub)} 
                      style={{ cursor: 'pointer', transition: 'background-color 0.15s' }}
                      title="คลิกเพื่อดูรายละเอียดไฟล์ข้อสอบ"
                    >
                      <td><strong style={{ fontFamily: 'monospace', color: 'var(--primary)' }}>{sub.code}</strong></td>
                      <td><span style={{ fontWeight: '600' }}>{sub.name}</span></td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          background: subFiles.length > 0 ? 'rgba(0,184,148,0.1)' : 'rgba(0,0,0,0.03)',
                          color: subFiles.length > 0 ? 'var(--primary)' : 'var(--text-muted)',
                          padding: '2px 8px', borderRadius: '12px', fontWeight: '700'
                        }}>
                          {subFiles.length} ไฟล์
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {uploaders.length > 0 && subFiles.length > 0 ? (
                            uploaders.map((up, idx) => (
                              <span key={idx} style={{
                                background: 'var(--bg-base)',
                                border: '1px solid var(--border)',
                                padding: '2px 8px',
                                borderRadius: '8px',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: 'var(--text-secondary)'
                              }}>
                                👤 {up}
                              </span>
                            ))
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>ยังไม่มีครูส่งข้อสอบ</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              })()}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        {subjects.length > 10 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              แสดง {((subjectPage - 1) * 10) + 1} - {Math.min(subjects.length, subjectPage * 10)} จาก {subjects.length} วิชา
            </span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setSubjectPage(prev => Math.max(1, prev - 1))}
                disabled={subjectPage === 1}
                style={{ padding: '6px 12px', fontSize: '12px', height: '32px' }}
              >
                ◀ ย้อนกลับ
              </button>
              <span style={{ fontSize: '13px', fontWeight: '600' }}>
                หน้า {subjectPage} / {Math.ceil(subjects.length / 10)}
              </span>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setSubjectPage(prev => Math.min(Math.ceil(subjects.length / 10), prev + 1))}
                disabled={subjectPage === Math.ceil(subjects.length / 10)}
                style={{ padding: '6px 12px', fontSize: '12px', height: '32px' }}
              >
                ถัดไป ▶
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drag & Drop Zone (when empty) */}
      {!loading && files.length === 0 && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragging ? '#6366f1' : 'var(--border)'}`,
            borderRadius: '16px', padding: '60px 40px', textAlign: 'center',
            cursor: 'pointer', transition: 'all 0.2s',
            background: isDragging ? 'rgba(99,102,241,0.06)' : 'transparent'
          }}
        >
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>
            {isDragging ? '📂' : '📤'}
          </div>
          <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>
            {isDragging ? 'วางไฟล์ที่นี่เลย!' : 'ยังไม่มีไฟล์ข้อสอบในคลัง'}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px' }}>
            คลิกเพื่อเลือกไฟล์ หรือ ลาก-วางไฟล์ PDF / Word มาที่นี่
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: '#fff', padding: '10px 24px', borderRadius: '10px',
            fontWeight: '700', fontSize: '14px'
          }}>
            ⬆️ อัปโหลดข้อสอบ
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
          กำลังโหลดคลังข้อสอบ...
        </div>
      )}

      {/* No results */}
      {!loading && files.length > 0 && filteredFiles.length === 0 && (
        <div className="glassmorphism" style={{ padding: '40px', textAlign: 'center', borderRadius: '16px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</div>
          <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>ไม่พบไฟล์ที่ตรงกับการค้นหา</div>
          <button type="button" className="btn btn-outline"
            onClick={() => { setSearchQuery(''); setFilterSubject(''); setFilterType('') }}
            style={{ marginTop: '12px', fontSize: '13px' }}>
            ล้างตัวกรองทั้งหมด
          </button>
        </div>
      )}

      {/* File List */}
      {!loading && filteredFiles.length > 0 && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            display: 'flex', flexDirection: 'column', gap: '10px',
            border: isDragging ? '2px dashed #6366f1' : '2px dashed transparent',
            borderRadius: '16px', padding: isDragging ? '16px' : '0',
            transition: 'all 0.2s',
            background: isDragging ? 'rgba(99,102,241,0.04)' : 'transparent'
          }}
        >
          {isDragging && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6366f1', fontWeight: '700' }}>
              📂 วางไฟล์เพื่ออัปโหลด
            </div>
          )}
          {filteredFiles.map(file => {
            const badge = getFileBadgeStyle(file.file_type)
            const icon = getFileIcon(file.file_type)
            const isOwner = file.uploaded_by === user?.id
            const canEdit = user?.role === 'admin' || isOwner
            
            // Files are synchronized via Supabase Storage in production, so everyone can download
            const isLocalOnOtherUser = false

            return (
              <div key={file.id}
                className="glassmorphism"
                style={{
                  padding: '16px 20px', borderRadius: '14px',
                  display: 'flex', gap: '14px', alignItems: 'flex-start',
                  transition: 'transform 0.15s, box-shadow 0.15s'
                }}
                onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                {/* File Icon */}
                <div style={{
                  width: '46px', height: '46px', borderRadius: '12px',
                  background: badge.bg, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '22px', flexShrink: 0
                }}>{icon}</div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>{file.name}</span>
                    <span style={{
                      background: badge.bg, color: badge.color,
                      padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '700'
                    }}>{badge.label}</span>
                    
                    {/* Storage Type Badge */}
                    <span style={{
                      background: file.storage_type === 'google_drive' ? 'rgba(52,168,83,0.12)' : 'rgba(99,102,241,0.12)',
                      color: file.storage_type === 'google_drive' ? '#16a34a' : 'var(--primary)',
                      padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '700'
                    }}>
                      {file.storage_type === 'google_drive' ? '🌐 Google Drive' : '💻 ในเครื่อง'}
                    </span>

                    {file.subject_name && file.subject_name !== 'ทั่วไป' && (
                      <span style={{
                        background: 'rgba(99,102,241,0.1)', color: '#6366f1',
                        padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '700'
                      }}>📚 {file.subject_name}</span>
                    )}
                  </div>
                  {file.description && (
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', lineHeight: 1.4 }}>
                      {file.description}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '14px', fontSize: '11px', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                    <span>📁 {file.file_name}</span>
                    <span>📦 {formatBytes(file.file_size)}</span>
                    <span>🕐 {formatDate(file.created_at)}</span>
                    {file.uploader_name && <span>👤 {file.uploader_name}</span>}
                  </div>
                  {file.tags && file.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px', marginTop: '8px', flexWrap: 'wrap' }}>
                      {file.tags.map((tag, ti) => (
                        <span key={ti} style={{
                          background: 'rgba(16,185,129,0.1)', color: 'var(--primary)',
                          padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '600'
                        }}>#{tag}</span>
                      ))}
                    </div>
                  )}

                  {isLocalOnOtherUser && (
                    <div style={{ fontSize: '11px', color: 'var(--warning)', marginTop: '8px', fontWeight: '600' }}>
                      ⚠️ ไฟล์นี้เก็บในเครื่องของคุณครูผู้เขียน และยังไม่ได้แชร์ลง Google Drive (คุณจะไม่สามารถเปิดดูหรือดาวน์โหลดได้)
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {file.file_type.includes('pdf') && !isLocalOnOtherUser && (
                    <button type="button" onClick={() => handlePreview(file)}
                      className="btn btn-outline"
                      style={{ padding: '7px 12px', fontSize: '12px', fontWeight: '600' }}
                      title="เปิดดูไฟล์">
                      👁️ ดูไฟล์
                    </button>
                  )}
                  <button type="button" onClick={() => handleDownload(file)}
                    disabled={isLocalOnOtherUser}
                    className="btn btn-outline"
                    style={{ 
                      padding: '7px 12px', 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: isLocalOnOtherUser ? 'var(--text-muted)' : '#059669', 
                      borderColor: isLocalOnOtherUser ? 'var(--border)' : '#059669',
                      cursor: isLocalOnOtherUser ? 'not-allowed' : 'pointer'
                    }}
                    title={isLocalOnOtherUser ? 'ไฟล์อยู่นอกระยะการเข้าถึง' : 'ดาวน์โหลด'}>
                    ⬇️ ดาวน์โหลด
                  </button>
                  {canEdit && (
                    <>
                      <button type="button" onClick={() => openEdit(file)}
                        className="btn btn-outline"
                        style={{ padding: '7px 12px', fontSize: '12px', fontWeight: '600', color: '#6366f1', borderColor: '#6366f1' }}
                        title="แก้ไข">
                        ✏️ แก้ไข
                      </button>
                      <button type="button" onClick={() => setDeletingFile(file)}
                        className="btn btn-outline"
                        style={{ padding: '7px 12px', fontSize: '12px', fontWeight: '600', color: 'var(--danger)', borderColor: 'var(--danger)' }}
                        title="ลบ">
                        🗑️
                      </button>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ────── Upload Modal ────── */}
      {showUploadModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: '20px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '20px', padding: '32px',
            maxWidth: '560px', width: '100%', border: '1px solid var(--border)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.25)'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '4px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontSize: '24px' }}>⬆️</span> อัปโหลดข้อสอบ
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
              {isGoogleConnected 
                ? 'ไฟล์จะถูกอัปโหลดขึ้น Google Drive ของคุณโดยตรงเพื่อแชร์ข้อมูลร่วมกัน' 
                : 'คุณยังไม่ได้เชื่อมต่อ Google Drive (ไฟล์จะบันทึกไว้ในเบราว์เซอร์เครื่องนี้เท่านั้น)'}
            </p>

            {/* Selected file chip */}
            {selectedFile && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.08))',
                border: '1px solid rgba(99,102,241,0.25)',
                borderRadius: '12px', padding: '12px 16px', marginBottom: '20px',
                display: 'flex', gap: '12px', alignItems: 'center'
              }}>
                <span style={{ fontSize: '24px' }}>{getFileIcon(selectedFile.type)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedFile.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{formatBytes(selectedFile.size)}</div>
                </div>
                <span style={{
                  background: getFileBadgeStyle(selectedFile.type).bg,
                  color: getFileBadgeStyle(selectedFile.type).color,
                  padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700'
                }}>{getFileBadgeStyle(selectedFile.type).label}</span>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label className="input-label">ชื่อข้อสอบ <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input type="text" value={uploadName} onChange={e => setUploadName(e.target.value)}
                  placeholder="เช่น ข้อสอบวิทยาศาสตร์ ม.5 ภาคเรียน 1/2567"
                  className="input-field" style={{ width: '100%' }} />
              </div>
              <div>
                <label className="input-label">รายละเอียด</label>
                <textarea rows={2} value={uploadDescription} onChange={e => setUploadDescription(e.target.value)}
                  placeholder="หมายเหตุหรือคำอธิบายเพิ่มเติม (ไม่บังคับ)"
                  className="input-field" style={{ width: '100%', resize: 'none' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="input-label">วิชา</label>
                  <select value={uploadSubjectId} onChange={e => setUploadSubjectId(e.target.value)}
                    className="input-field" style={{ width: '100%' }}>
                    <option value="">— ไม่ระบุ —</option>
                    {subjects.map((s: any) => (
                      <option key={s.id} value={String(s.id)}>[{s.code}] {s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="input-label">แท็ก (คั่น ,)</label>
                  <input type="text" value={uploadTags} onChange={e => setUploadTags(e.target.value)}
                    placeholder="Pre-Test, 2567, ม.5"
                    className="input-field" style={{ width: '100%' }} />
                </div>
              </div>
              {uploadSubjectId && (
                <div style={{
                  padding: '12px 16px',
                  background: 'var(--bg-base, #f8fafc)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  marginTop: '4px'
                }}>
                  <label className="input-label" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700' }}>
                    <span>👥</span> เลือกครูร่วมสอนในวิชานี้ที่สามารถดู/แก้ไขข้อสอบนี้ได้
                  </label>
                  {(() => {
                    const sameSubjectAssignments = teachingAssignments.filter(
                      (a: any) => a.subject_id === Number(uploadSubjectId) && a.teacher_id !== user?.id
                    )
                    const uniqueTeacherIds = Array.from(new Set(sameSubjectAssignments.map((a: any) => a.teacher_id)))
                    const coTeachers = profiles.filter((p: any) => uniqueTeacherIds.includes(p.id))

                    if (coTeachers.length === 0) {
                      return <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>ไม่มีครูท่านอื่นลงทะเบียนสอนวิชานี้</p>
                    }

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '120px', overflowY: 'auto', paddingRight: '4px' }}>
                        {coTeachers.map((t: any) => {
                          const tagValue = `co_teacher:${t.id}`
                          const currentTags = uploadTags.split(',').map(tag => tag.trim()).filter(Boolean)
                          const isChecked = currentTags.includes(tagValue)

                          const handleCheckboxChange = (checked: boolean) => {
                            let newTags = [...currentTags]
                            if (checked) {
                              if (!newTags.includes(tagValue)) newTags.push(tagValue)
                            } else {
                              newTags = newTags.filter(x => x !== tagValue)
                            }
                            setUploadTags(newTags.join(', '))
                          }

                          return (
                            <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)' }}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => handleCheckboxChange(e.target.checked)}
                                style={{ accentColor: 'var(--primary)' }}
                              />
                              <span>{t.first_name} {t.last_name} ({t.username})</span>
                            </label>
                          )
                        })}
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>

            {/* Progress */}
            {uploading && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>
                  <span>กำลังอัปโหลด...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: '20px', height: '8px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: '20px',
                    background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                    width: `${uploadProgress}%`, transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" onClick={closeUploadModal} className="btn btn-outline" disabled={uploading}>
                ยกเลิก
              </button>
              <button type="button" onClick={handleUpload}
                disabled={uploading || !uploadName.trim() || !selectedFile}
                style={{
                  background: uploading || !uploadName.trim() ? 'rgba(99,102,241,0.4)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '10px',
                  fontWeight: '700', fontSize: '14px', cursor: uploading || !uploadName.trim() ? 'not-allowed' : 'pointer'
                }}>
                {uploading 
                  ? `⏳ กำลังอัปโหลด ${uploadProgress}%` 
                  : isGoogleConnected ? '🌐 อัปโหลดขึ้น Google Drive' : '⬆️ อัปโหลดลงเครื่อง'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────── Edit Modal ────── */}
      {editingFile && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: '20px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '20px', padding: '32px',
            maxWidth: '520px', width: '100%', border: '1px solid var(--border)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.25)'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span>✏️</span> แก้ไขข้อมูลไฟล์
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label className="input-label">ชื่อข้อสอบ <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                  className="input-field" style={{ width: '100%' }} />
              </div>
              <div>
                <label className="input-label">รายละเอียด</label>
                <textarea rows={2} value={editDescription} onChange={e => setEditDescription(e.target.value)}
                  className="input-field" style={{ width: '100%', resize: 'none' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="input-label">วิชา</label>
                  <select value={editSubjectId} onChange={e => setEditSubjectId(e.target.value)}
                    className="input-field" style={{ width: '100%' }}>
                    <option value="">— ไม่ระบุ —</option>
                    {subjects.map((s: any) => (
                      <option key={s.id} value={String(s.id)}>[{s.code}] {s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="input-label">แท็ก (คั่น ,)</label>
                  <input type="text" value={editTags} onChange={e => setEditTags(e.target.value)}
                    className="input-field" style={{ width: '100%' }} />
                </div>
              </div>
              {editSubjectId && (
                <div style={{
                  padding: '12px 16px',
                  background: 'var(--bg-base, #f8fafc)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  marginTop: '4px'
                }}>
                  <label className="input-label" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700' }}>
                    <span>👥</span> เลือกครูร่วมสอนในวิชานี้ที่สามารถดู/แก้ไขข้อสอบนี้ได้
                  </label>
                  {(() => {
                    const sameSubjectAssignments = teachingAssignments.filter(
                      (a: any) => a.subject_id === Number(editSubjectId) && a.teacher_id !== user?.id
                    )
                    const uniqueTeacherIds = Array.from(new Set(sameSubjectAssignments.map((a: any) => a.teacher_id)))
                    const coTeachers = profiles.filter((p: any) => uniqueTeacherIds.includes(p.id))

                    if (coTeachers.length === 0) {
                      return <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>ไม่มีครูท่านอื่นที่ลงทะเบียนสอนวิชานี้</p>
                    }

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '120px', overflowY: 'auto', paddingRight: '4px' }}>
                        {coTeachers.map((t: any) => {
                          const tagValue = `co_teacher:${t.id}`
                          const currentTags = editTags.split(',').map(tag => tag.trim()).filter(Boolean)
                          const isChecked = currentTags.includes(tagValue)

                          const handleCheckboxChange = (checked: boolean) => {
                            let newTags = [...currentTags]
                            if (checked) {
                              if (!newTags.includes(tagValue)) newTags.push(tagValue)
                            } else {
                              newTags = newTags.filter(x => x !== tagValue)
                            }
                            setEditTags(newTags.join(', '))
                          }

                          return (
                            <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)' }}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => handleCheckboxChange(e.target.checked)}
                                style={{ accentColor: 'var(--primary)' }}
                              />
                              <span>{t.first_name} {t.last_name} ({t.username})</span>
                            </label>
                          )
                        })}
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" onClick={() => setEditingFile(null)} className="btn btn-outline">ยกเลิก</button>
              <button type="button" onClick={handleEdit} disabled={!editName.trim()}
                className="btn btn-primary" style={{ opacity: !editName.trim() ? 0.5 : 1 }}>
                💾 บันทึกการแก้ไข
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────── Delete Confirm Modal ────── */}
      {deletingFile && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: '20px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '20px', padding: '36px',
            maxWidth: '400px', width: '100%', border: '1px solid var(--border)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.25)', textAlign: 'center'
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              background: 'rgba(239,68,68,0.1)', margin: '0 auto 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '28px'
            }}>🗑️</div>
            <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '10px' }}>ยืนยันการลบไฟล์</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '6px' }}>ต้องการลบไฟล์</p>
            <p style={{ fontWeight: '700', fontSize: '15px', marginBottom: '6px', color: 'var(--text-primary)' }}>
              "{deletingFile.name}"
            </p>
            <p style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '24px' }}>
              ⚠️ ไฟล์จะถูกลบออกจากระบบและไม่สามารถกู้คืนได้
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button type="button" onClick={() => setDeletingFile(null)} className="btn btn-outline" disabled={deleting}>
                ยกเลิก
              </button>
              <button type="button" onClick={handleDelete} disabled={deleting}
                style={{
                  background: 'var(--danger)', color: '#fff', border: 'none',
                  padding: '10px 24px', borderRadius: '10px', fontWeight: '700',
                  fontSize: '14px', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1
                }}>
                {deleting ? '⏳ กำลังลบ...' : '🗑️ ยืนยันลบ'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ────── Subject Files Modal ────── */}
      {selectedSubjectForModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '20px', padding: '32px',
            maxWidth: '750px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
            border: '1px solid var(--border)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
            color: 'var(--text-primary)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '800', margin: 0, display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '24px' }}>📚</span> รายรายละเอียดไฟล์ข้อสอบรายวิชา
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px', marginBottom: 0 }}>
                  รหัสวิชา: <strong>{selectedSubjectForModal.code}</strong> — {selectedSubjectForModal.name}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedSubjectForModal(null)}
                style={{
                  background: 'transparent', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--text-secondary)'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px', display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
              {(() => {
                const subjectFiles = files.filter(f => f.subject_id === selectedSubjectForModal.id)
                if (subjectFiles.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                      <span style={{ fontSize: '48px', display: 'block', marginBottom: '10px' }}>📭</span>
                      ยังไม่มีไฟล์ข้อสอบอัปโหลดในวิชานี้
                    </div>
                  )
                }

                return subjectFiles.map(file => {
                  const badge = getFileBadgeStyle(file.file_type)
                  const icon = getFileIcon(file.file_type)
                  
                  const myAssignments = teachingAssignments.filter((a: any) => a.teacher_id === user?.id)
                  const mySubjectIds = Array.from(new Set(myAssignments.map((a: any) => a.subject_id))) as number[]
                  const isCoTeacher = file.subject_id && mySubjectIds.includes(file.subject_id)
                  const isSharedExplicitly = file.tags?.includes(`co_teacher:${user?.id}`)

                  const canEdit = user?.role === 'admin' || 
                                  user?.role === 'head_of_department' || 
                                  file.uploaded_by === user?.id || 
                                  isCoTeacher || 
                                  isSharedExplicitly
                  
                  const isLocalOnOtherUser = file.storage_type === 'local' && 
                                             file.uploaded_by !== user?.id && 
                                             user?.role !== 'admin' && 
                                             !isCoTeacher && 
                                             !isSharedExplicitly

                  return (
                    <div
                      key={file.id}
                      style={{
                        background: 'var(--bg-base, #f8fafc)',
                        border: '1px solid var(--border)',
                        borderRadius: '12px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '16px'
                      }}
                    >
                      {/* Left: icon container matching screenshot 3 (light background container) */}
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '10px',
                        background: badge.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                        flexShrink: 0
                      }}>
                        {icon}
                      </div>

                      {/* Middle: Details */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                          <span style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>
                            {file.name}
                          </span>
                          <span style={{
                            background: badge.bg,
                            color: badge.color,
                            padding: '2px 8px',
                            borderRadius: '20px',
                            fontSize: '10px',
                            fontWeight: '700'
                          }}>{badge.label}</span>
                          <span style={{
                            background: file.storage_type === 'google_drive' ? 'rgba(52,168,83,0.12)' : 'rgba(99,102,241,0.12)',
                            color: file.storage_type === 'google_drive' ? '#16a34a' : 'var(--primary)',
                            padding: '2px 8px',
                            borderRadius: '20px',
                            fontSize: '10px',
                            fontWeight: '700'
                          }}>
                            {file.storage_type === 'google_drive' ? '🌐 Google Drive' : '💻 ในเครื่อง'}
                          </span>
                        </div>
                        
                        {/* File meta line similar to screenshot 3 */}
                        <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                          <span>📁 {file.file_name}</span>
                          <span>📦 {formatBytes(file.file_size)}</span>
                          <span>🕒 {formatDate(file.created_at)}</span>
                          {file.uploader_name && <span>👤 {file.uploader_name}</span>}
                        </div>
                        
                        {isLocalOnOtherUser && (
                          <div style={{ fontSize: '11px', color: 'var(--warning)', marginTop: '4px', fontWeight: '600' }}>
                            ⚠️ ไฟล์เก็บเฉพาะที่เครื่องของผู้เขียน (เข้าถึงไม่ได้)
                          </div>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        {file.file_type.includes('pdf') && !isLocalOnOtherUser && (
                          <button
                            type="button"
                            onClick={() => handlePreview(file)}
                            className="btn btn-outline"
                            style={{
                              padding: '8px 12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            👁️ ดูไฟล์
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDownload(file)}
                          disabled={isLocalOnOtherUser}
                          className="btn btn-outline"
                          style={{
                            padding: '8px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: isLocalOnOtherUser ? 'var(--text-muted)' : '#059669',
                            borderColor: isLocalOnOtherUser ? 'var(--border)' : '#059669',
                            cursor: isLocalOnOtherUser ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          📥 ดาวน์โหลด
                        </button>
                        {canEdit && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedSubjectForModal(null)
                                openEdit(file)
                              }}
                              className="btn btn-outline"
                              style={{ padding: '8px 12px', fontSize: '12px', fontWeight: '600', color: '#6366f1', borderColor: '#6366f1' }}
                            >
                              ✏️ แก้ไข
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedSubjectForModal(null)
                                setDeletingFile(file)
                              }}
                              className="btn btn-outline"
                              style={{ padding: '8px 12px', fontSize: '12px', fontWeight: '600', color: 'var(--danger)', borderColor: 'var(--danger)' }}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })
              })()}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                type="button"
                onClick={() => setSelectedSubjectForModal(null)}
                className="btn btn-outline"
                style={{ padding: '10px 24px', fontSize: '14px', fontWeight: '700', borderRadius: '10px' }}
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExamRepository
